import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LOGO_PATH, BUSINESS_NAME } from '../../config/brand';
import { LayoutDashboard, CalendarCheck, Calendar, Users, PawPrint, Layers, UserCog, BarChart3, Settings, LogOut, Scissors, IndianRupee, MessageSquare, CalendarOff, FileText, Menu, X, Clock } from 'lucide-react';
import { useState } from 'react';

const sidebarItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/upcoming', label: 'Upcoming', icon: Clock },
  { path: '/admin/bookings', label: 'Bookings', icon: CalendarCheck },
  { path: '/admin/calendar', label: 'Calendar', icon: Calendar },
  { path: '/admin/clients', label: 'Clients', icon: Users },
  { path: '/admin/pets', label: 'Pets', icon: PawPrint },
  { path: '/admin/services', label: 'Services', icon: Layers },
  { path: '/admin/pricing', label: 'Pricing', icon: IndianRupee },
  { path: '/admin/invoices', label: 'Invoices', icon: FileText },
  { path: '/admin/blocked-slots', label: 'Blocked Slots', icon: CalendarOff },
  { path: '/admin/messaging', label: 'Messaging', icon: MessageSquare },
  { path: '/admin/staff', label: 'Staff', icon: UserCog },
  { path: '/admin/reports', label: 'Reports', icon: BarChart3 },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const Sidebar = ({ mobile = false }) => (
    <div className={`${mobile ? 'w-full' : 'w-[260px] min-h-screen'} bg-white border-r border-[#E5E5E0] flex flex-col`}>
      <div className="p-5 flex items-center justify-between">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/admin/dashboard')}>
          <img src={LOGO_PATH} alt={BUSINESS_NAME} className="h-10 object-contain" />
          <span className="text-xs bg-[#A96B49] text-white px-2 py-0.5 rounded-full font-medium">Admin</span>
        </div>
        {mobile && (
          <button onClick={() => setMobileOpen(false)}><X className="w-5 h-5 text-[#5A5A5A]" /></button>
        )}
      </div>

      <nav className="flex-1 px-3 py-2 space-y-1">
        {sidebarItems.map((item) => {
          const active = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => { navigate(item.path); if (mobile) setMobileOpen(false); }}
              className={`sidebar-item w-full ${active ? 'active' : ''}`}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-[#E5E5E0]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-[#E2E8E4] rounded-full flex items-center justify-center text-sm font-semibold text-[#2D2D2D]">
            {user?.name?.[0] || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#2D2D2D] truncate">{user?.name}</p>
            <p className="text-xs text-[#888] truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-[#888] hover:text-[#D9534F] w-full px-2 py-1.5 rounded-lg hover:bg-[#FEE2E2]/50" data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block sticky top-0 h-screen overflow-y-auto">
        <Sidebar />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Mobile Top Bar */}
        <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#E5E5E0] px-4 h-14 flex items-center gap-3">
          <button onClick={() => setMobileOpen(true)} data-testid="admin-menu-btn">
            <Menu className="w-5 h-5 text-[#3D2418]" />
          </button>
          <img src={LOGO_PATH} alt={BUSINESS_NAME} className="h-8 object-contain" />
        </div>

        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
